/**
 * iter1213 - ConfirmOpeningBalanceDialog
 *
 * Purpose-built modal that walks the user through supplying the opening
 * balance for a Plaid-connected account whose auto-derivation was deferred
 * by the completeness gates in `_derive_plaid_opening_balance`. It is the
 * last-resort escape hatch — used only when the platform cannot safely
 * derive OB from Plaid data alone.
 *
 * Design rules per user brief:
 *  - MANDATORY. No dismiss/close-without-answer path.
 *  - Explains WHAT the accounting start date is for this business.
 *  - Explains WHERE to find the opening balance (closing balance on the day
 *    BEFORE the accounting start date, from the user's bank statement).
 *  - Optionally shows the platform's best-effort estimate but clearly labels
 *    it as unverified so the user isn't primed to accept it blindly.
 */
import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { CalendarDays, FileText, HelpCircle, Info, Loader2, AlertCircle } from 'lucide-react';

const GATE_EXPLANATIONS = {
  gate1_historical_not_ready: "Your bank hasn't finished sending us the full transaction history yet.",
  gate2_no_post_start_txns: "We haven't received transactions dated on or after your accounting start date yet.",
  gate2_no_pre_start_txns_historical_incomplete: "Only your recent transactions are visible — historical data is still loading.",
  gate2_no_txns_at_all: "No transactions have arrived from the bank yet.",
  gate3_balance_unstable: "Your bank reported a different balance across two syncs. We're waiting for it to settle.",
  gate3_awaiting_second_observation: "We need a second balance reading from the bank before we can be confident.",
  gate4_paths_disagree: "Two independent calculations of the opening balance disagreed because of unsettled pending transactions.",
  journal_post_failed: "The opening balance couldn't be posted automatically.",
};

function niceDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
}

export const ConfirmOpeningBalanceDialog = ({ task, open, onSaved }) => {
  const [amount, setAmount] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const bankLabel = useMemo(() => {
    if (!task) return '';
    const num = task.account_number ? ` (…${String(task.account_number).slice(-4)})` : '';
    return `${task.bank_name || task.account_name || 'Bank account'}${num}`;
  }, [task]);

  const gateWhy = task ? (GATE_EXPLANATIONS[task.gate_reason] ||
    'Automatic derivation could not proceed safely.') : '';

  const handleSave = async () => {
    if (!task) return;
    const value = parseFloat(amount);
    if (Number.isNaN(value)) {
      toast.error('Please enter a valid amount from your bank statement');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/bank-accounts/${task.bank_account_id}/opening-balance`, {
        amount: value,
      });
      toast.success('Opening balance confirmed and posted to your books');
      setAmount('');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message ||
        err.response?.data?.detail ||
        'Failed to save opening balance');
    } finally {
      setSaving(false);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={() => {}} modal>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="confirm-opening-balance-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Confirm opening balance
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Bank card + gate reason */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm font-medium text-amber-900" data-testid="ob-dialog-bank-name">
              {bankLabel}
            </p>
            <p className="text-xs text-amber-800 mt-1">{gateWhy}</p>
          </div>

          {/* What we're asking for */}
          <div className="text-sm text-slate-700 space-y-2">
            <div className="flex items-start gap-2">
              <CalendarDays className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p>
                  Your accounting start date is{' '}
                  <strong data-testid="ob-dialog-start-date">
                    {niceDate(task.accounting_start_date)}
                  </strong>.
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Please enter the closing balance shown on your bank statement for{' '}
                  <strong data-testid="ob-dialog-ob-date">
                    {niceDate(task.ob_journal_date)}
                  </strong>{' '}
                  (the day BEFORE your accounting start date).
                </p>
              </div>
            </div>

            {typeof task.best_estimate_bank_ccy === 'number' &&
             Math.abs(task.best_estimate_bank_ccy) > 0.005 && (
              <div className="flex items-start gap-2 bg-slate-50 rounded-md p-2 border border-slate-200">
                <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
                <div className="text-xs text-slate-600">
                  Our best-effort estimate is{' '}
                  <span className="font-mono font-semibold text-slate-800">
                    {task.currency} {Number(task.best_estimate_bank_ccy).toLocaleString(
                      undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>{' '}
                  — but we could not verify it. Please compare against your statement before accepting.
                </div>
              </div>
            )}
          </div>

          {/* Where to find it */}
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-800 hover:underline"
            data-testid="ob-dialog-help-toggle"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            {showHelp ? 'Hide' : 'Where do I find this?'}
          </button>
          {showHelp && (
            <div className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-md p-3 space-y-1.5"
                 data-testid="ob-dialog-help-body">
              <div className="flex items-start gap-1.5">
                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
                <span><strong>Bank statement PDF</strong> &mdash; open the statement covering{' '}
                  {niceDate(task.ob_journal_date)} and read the closing balance for that day.</span>
              </div>
              <div className="flex items-start gap-1.5">
                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
                <span><strong>Bank mobile app</strong> &mdash; navigate to statements, filter to{' '}
                  {niceDate(task.ob_journal_date)}, and note the &ldquo;balance&rdquo; on that line.</span>
              </div>
              <div className="flex items-start gap-1.5">
                <FileText className="w-3.5 h-3.5 mt-0.5 shrink-0 text-blue-600" />
                <span>Enter in <strong>{task.currency || 'account currency'}</strong>. Use a negative
                  number if the account was overdrawn. Use <strong>0</strong> if the account was empty or
                  newly opened.</span>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="space-y-1">
            <label htmlFor="ob-amount" className="text-xs font-medium text-slate-600">
              Opening balance on {niceDate(task.ob_journal_date)}
            </label>
            <div className="flex gap-2 items-center">
              <span className="text-sm font-mono text-slate-500 w-12 text-right">
                {task.currency || ''}
              </span>
              <Input
                id="ob-amount"
                data-testid="ob-dialog-amount-input"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="flex-1"
                autoFocus
              />
            </div>
          </div>

          {/* CTA (no cancel — mandatory) */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={saving || amount === ''}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="ob-dialog-confirm-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : 'Confirm & post'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmOpeningBalanceDialog;
